import { useState, useRef, useCallback } from 'react';
import { getAccessToken, triggerReauth } from '../../../auth/AuthProvider';
import { createConcurrencyPool } from '../../../utils/concurrencyPool';
import { createCoverFileEntry } from '../utils/coverFileUtils';

const apiBaseUrl = process.env.REACT_APP_ADD_COVER_URL
  || process.env.REACT_APP_API_URL
  || window.location.origin;

// Bounds how many /api/cover/bind requests this client keeps in flight at
// once, regardless of how many files a user selects — protects the single
// (desired_count = 1) backend task from being overwhelmed by one browser
// session's batch. Files beyond this stay 'queued' until a slot frees up.
const MAX_CONCURRENT_UPLOADS = 3;

// Must match CoverController.DIMENSION_INFO_HEADER, and be listed in CorsConfig's
// exposedHeaders — otherwise this comes back null cross-origin even though the
// header is genuinely present on the response.
const DIMENSION_INFO_HEADER = 'X-Cover-Dimension-Info';

async function readBlobTextSafely(blob) {
  try {
    const text = await blob.text();
    return text || null;
  } catch {
    return null;
  }
}

function readDimensionInfo(xhr) {
  const raw = xhr.getResponseHeader(DIMENSION_INFO_HEADER);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function statusToMessage(status) {
  switch (status) {
    case 400: return 'No EAN-13 found in filename';
    case 404: return 'Product or cover image not found for this EAN';
    case 422: return 'Product data incomplete (title or cover key missing)';
    case 413: return "File exceeds the server's size limit";
    default: return `HTTP ${status}`;
  }
}

export function useCoverBatchUpload() {
  const [files, setFiles] = useState([]);
  const poolRef = useRef(createConcurrencyPool(MAX_CONCURRENT_UPLOADS));

  const updateStage = useCallback((id, stageName, patch) => {
    setFiles((prev) => prev.map((entry) => (
      entry.id === id
        ? { ...entry, stages: { ...entry.stages, [stageName]: { ...entry.stages[stageName], ...patch } } }
        : entry
    )));
  }, []);

  // Raw XHR (not apiFetch) so we can observe upload progress, matching the
  // pattern already used by ImageCountForm/hooks/useImageCount.js.
  const uploadOne = useCallback(async (id, file) => {
    updateStage(id, 'cover', { status: 'uploading', progressPct: 0, error: null, resultBlob: null, dimensionInfo: null });
    const token = await getAccessToken();

    await new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${apiBaseUrl}/api/cover/bind`);
      xhr.responseType = 'blob';
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          updateStage(id, 'cover', { status: 'uploading', progressPct: Math.round((e.loaded / e.total) * 100) });
        }
      };
      xhr.upload.onload = () => updateStage(id, 'cover', { status: 'processing', progressPct: 100 });

      xhr.onload = async () => {
        if (xhr.status === 401) {
          // Same handling as apiFetch.js: session is actually over, redirect
          // to login instead of surfacing a confusing per-file error.
          triggerReauth();
          resolve();
          return;
        }
        // Present whenever the backend actually got far enough to measure both files
        // (success, or a dimension mismatch) — absent for earlier failures (no product,
        // no cover found, etc.) where there was nothing to compare yet.
        const dimensionInfo = readDimensionInfo(xhr);
        if (xhr.status === 200) {
          updateStage(id, 'cover', { status: 'done', error: null, resultBlob: xhr.response, dimensionInfo });
        } else {
          // Every error case's body now carries a specific message (see
          // CoverController's textResponse) — statusToMessage is only a fallback
          // for the rare case a body comes back empty.
          const serverMessage = await readBlobTextSafely(xhr.response);
          updateStage(id, 'cover', { status: 'error', error: serverMessage || statusToMessage(xhr.status), dimensionInfo });
        }
        resolve();
      };
      xhr.onerror = () => {
        updateStage(id, 'cover', { status: 'error', error: 'Network error' });
        resolve();
      };

      const formData = new FormData();
      formData.append('file', file);
      xhr.send(formData);
    });
  }, [updateStage]);

  const addFiles = useCallback((newFiles) => {
    const entries = newFiles.map(createCoverFileEntry);
    setFiles((prev) => [...prev, ...entries]);
    entries.forEach((entry) => {
      if (entry.stages.cover.status === 'queued') {
        poolRef.current.push(() => uploadOne(entry.id, entry.file));
      }
    });
  }, [uploadOne]);

  const retry = useCallback((entry) => {
    updateStage(entry.id, 'cover', { status: 'queued', progressPct: 0, error: null, resultBlob: null, dimensionInfo: null });
    poolRef.current.push(() => uploadOne(entry.id, entry.file));
  }, [updateStage, uploadOne]);

  return { files, addFiles, retry };
}
